import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../utils/router/axiosInstance';
import { getPointPosition } from '../../../utils/mapCoordinates';
import { logger } from '../../../utils/logger';

const PointWithRadius = ({
  x,
  y,
  radius,
  users,
  planWidth,
  planHeight,
  mapWidth,
  mapHeight,
  calibration,
  entityType
}) => {
  const [data, setData] = useState([]);
  const isEmployee = entityType === 'employee' && users?.id;
  // 🔴 Тип метки СПРАШИВАЕТСЯ ЯВНО (ревью Codex WEB-18, High). Первая редакция
  // считала Wi-Fi точкой всё, что не сотрудник, — и забирала имя, подсказку и
  // ореол у меток ИСТОРИИ перемещений (`entityType="history"`,
  // MapComponentSingle): по следу человека рассыпалось «без имени».
  const isDot = entityType === 'dot';

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        if (isEmployee) {
          let response = await axiosInstance.get(`website/employee/info/${users.id}`);
          const nameParts = response.data.fio.split(' ');
          setData(nameParts.length > 1 ? nameParts[1] : response.data.fio);
        }
      } catch (error) {
        logger.error('Error fetching employee data:', error);
      }
    };

    fetchEmployeeData();
  }, [isEmployee, users]);

  const coordinates = getPointPosition({
    x,
    y,
    planWidth,
    planHeight,
    mapWidth,
    mapHeight,
    calibration
  });

  // WEB-18: имя Wi-Fi точки. Сервер зовёт его `ssid`, слой карты — `name`;
  // берём что есть, иначе BSSID, иначе честное «без имени» — пустая подпись
  // выглядела бы как обрезанная.
  const dotName = users?.ssid || users?.name || users?.bssid || 'без имени';

  /**
   * 🔴 Куда прижать подпись. Ярлык центрирован по точке и шириной до 128 px,
   * значит у самого края плана половина его уезжает за границу — а контейнер
   * карты режет всё, что вылезло (`overflow: hidden`). Ровно так и вышло на
   * первом же снимке: три точки из пяти стоят на границе (координаты у них за
   * пределами этажа), и их имена обрезались до «setup7B87.yn…».
   *
   * Порог — половина ярлыка плюс сам кружок. У края подпись прижимается к
   * той же стороне, а не к точке: читаемость важнее симметрии.
   */
  const EDGE = 72;
  const labelSide = (() => {
    const width = Number(mapWidth);
    if (!Number.isFinite(width) || width <= 0) return '';
    if (coordinates.left < EDGE) return ' pointLabelStart';
    if (coordinates.left > width - EDGE) return ' pointLabelEnd';
    return '';
  })();

  /**
   * 🔴 У НИЖНЕГО края подпись встаёт НАД точкой. Ниже неё места нет: там уже
   * поле контейнера, а у него скруглённые углы в 37px — ярлык в самом углу
   * срезался бы дугой, даже когда прямоугольником он формально помещается.
   * Точка с y = 0 (у стены напротив входа) — ровно этот случай, и на бою такая
   * есть.
   */
  const LOW = 28;
  const labelAbove = Number.isFinite(Number(mapHeight)) && Number(mapHeight) > 0
    && coordinates.bottom < LOW ? ' pointLabelAbove' : '';

  return (
    <>
      {isEmployee ? (
        <div
          className="point"
          style={{
            left: `${coordinates.left}px`,
            bottom: `${coordinates.bottom}px`,
            transition: 'left 5s linear, bottom 5s linear'
          }}
        >
          <Link className="point-info" to={`/employee/${users.id}`}>
            {`Имя: ${data} Погр: ${radius.toFixed(2)}`}
          </Link>
        </div>
      ) : (
        /* 🔴 WEB-18: у Wi-Fi точки есть ИМЯ и может быть беда с координатами.
           До этого пять одинаковых кружков ничем не отличались, и понять, где
           `setup7B87.ynk`, а где `TP-Link_23E7_5G`, на карте было нельзя —
           приходилось уходить на страницу «Wi-Fi точки» и сверять по числам. */
        <div
          className={`point${isDot ? ' pointDot' : ''}${isDot && coordinates.clamped ? ' pointOutside' : ''}`}
          data-testid={isDot
            ? `map-dot-${users?.id ?? dotName}`
            : `map-mark-${users?.id ?? 'без-id'}`}
          style={{
            left: `${coordinates.left}px`,
            bottom: `${coordinates.bottom}px`,
            // 🔴 Wi-Fi точка НЕ ЕДЕТ (ревью Codex WEB-18, Medium): роутер вбит
            // руками и не движется, а пятисекундный переезд заставлял её
            // ползти к своему месту после каждой перерисовки плана — и делал
            // рендер-гейт зависимым от середины анимации.
            transition: isDot ? undefined : 'left 5s linear, bottom 5s linear',
            background: isDot ? 'red' : 'green'
          }}
          title={isDot
            ? (coordinates.clamped
              ? `${dotName} — координаты вне размеров этажа, точка прижата к краю плана`
              : dotName)
            : undefined}
        >
          {isDot && (
            <span className={`pointLabel${labelSide}${labelAbove}`}>{dotName}</span>
          )}
        </div>
      )}
    </>
  );
};

export default PointWithRadius;
